import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChevronDown, Eye } from 'lucide-react'
import { RiskProfileAssetsTable } from '../../RiskProfileAssetsTable'
import { RiskProfileSummaryTable } from '../../RiskProfileSummaryTable'
import { ExportAssetsCsvButton } from '../../ExportAssetsCsvButton'
import { useProfileAssetsData } from '../../../hooks/useProfileAssetsRequest'
import { useRiskProfileRequest } from '../../../hooks/useRiskProfileRequest'
import { useProfileTypeSummaryData } from '../../../hooks/useProfileSummaryRequest'
import { mapSummaryResponseToRows } from '../../../utils/riskProfileSummary.utils'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import { useRiskFilters } from '@app/modules/Risk/shared/hooks/useRiskFilters'
import { useEffect, useState } from 'react'

interface TypeResultsSectionProps {
  queryParams: RiskProfileQueryParams | null
}

export function TypeResultsSection({ queryParams }: TypeResultsSectionProps) {
  const { elementGroups } = useRiskFilters()
  const isSingleElement = elementGroups.length === 1
  const [activeTab, setActiveTab] = useState(isSingleElement ? 'top-elements' : 'types-summary')
  const assetsQuery = useProfileAssetsData(activeTab === 'top-elements' ? queryParams : null)
  const exportRequest = useRiskProfileRequest({ queryParams, widget: 'riskByHazardType' })
  const { query: summaryQuery } = useProfileTypeSummaryData(
    activeTab === 'types-summary' ? queryParams : null,
  )
  const summaryRows = summaryQuery.data ? mapSummaryResponseToRows(summaryQuery.data) : []

  useEffect(() => {
    setActiveTab(isSingleElement ? 'top-elements' : 'types-summary')
  }, [isSingleElement])

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <TabsList>
          <TabsTrigger value="types-summary">Types summary</TabsTrigger>
          <TabsTrigger value="top-elements">Top elements</TabsTrigger>
        </TabsList>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            className="inline-flex h-10 min-w-52 items-center justify-between gap-3 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted"
          >
            <span className="inline-flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              Columns
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          <ExportAssetsCsvButton request={exportRequest} />
        </div>
      </div>
      <TabsContent value="types-summary" className="!mt-3">
        <RiskProfileSummaryTable
          rows={summaryRows}
          labelHeader="Type"
          isLoading={summaryQuery.status === 'pending'}
        />
      </TabsContent>
      <TabsContent value="top-elements" className="!mt-3">
        <RiskProfileAssetsTable
          assets={assetsQuery.data ?? []}
          isLoading={assetsQuery.status === 'pending'}
        />
      </TabsContent>
    </Tabs>
  )
}
